import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';

export interface MultiSelectOption {
  id: number;
  name: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selectedIds: number[];
  onChange: (selectedIds: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedIds,
  onChange,
  placeholder = 'Selecione...',
  disabled = false,
  className = '',
  label,
  required = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOptions = options.filter(opt => selectedIds.includes(opt.id));

  const toggleOption = (optionId: number) => {
    if (selectedIds.includes(optionId)) {
      onChange(selectedIds.filter(id => id !== optionId));
    } else {
      onChange([...selectedIds, optionId]);
    }
  };

  const removeOption = (optionId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedIds.filter(id => id !== optionId));
  };

  const selectAll = () => {
    onChange(options.map(opt => opt.id));
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-[#cccccc] mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Selected items display */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`
          min-h-[42px] px-3 py-2 rounded-lg border transition-colors cursor-pointer
          ${disabled
            ? 'bg-[#1a1a1a] border-[#333333] text-[#666666] cursor-not-allowed'
            : 'bg-[#2a2a2a] border-[#444444] text-white hover:border-[#F4CC04]'
          }
          ${isOpen ? 'border-[#F4CC04]' : ''}
        `}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 flex flex-wrap gap-1">
            {selectedOptions.length === 0 ? (
              <span className="text-[#888888]">{placeholder}</span>
            ) : (
              selectedOptions.map(option => (
                <span
                  key={option.id}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-[#F4CC04] text-black text-xs font-medium rounded"
                >
                  {option.name}
                  {!disabled && (
                    <button
                      onClick={(e) => removeOption(option.id, e)}
                      className="hover:bg-black/10 rounded-full p-0.5"
                    >
                      <X size={12} />
                    </button>
                  )}
                </span>
              ))
            )}
          </div>
          <ChevronDown
            size={20}
            className={`text-[#888888] transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && !disabled && (
        <div
          className="absolute left-0 right-0 mt-1 bg-[#2a2a2a] border border-[#444444] rounded-lg shadow-2xl overflow-hidden z-50"
        >
          {/* Action buttons */}
          <div className="flex gap-2 p-2 border-b border-[#333333] bg-[#2a2a2a] sticky top-0 z-10">
            <button
              type="button"
              onClick={selectAll}
              className="flex-1 px-2 py-1 text-xs bg-[#1a1a1a] hover:bg-[#333333] text-white rounded transition-colors"
            >
              Selecionar Todos
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="flex-1 px-2 py-1 text-xs bg-[#1a1a1a] hover:bg-[#333333] text-white rounded transition-colors"
            >
              Limpar
            </button>
          </div>

          {/* Options list with scroll */}
          <div className="max-h-[220px] overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[#888888]">
                Nenhuma opção disponível
              </div>
            ) : (
              options.map(option => {
                const isSelected = selectedIds.includes(option.id);
                return (
                  <div
                    key={option.id}
                    onClick={() => toggleOption(option.id)}
                    className={`
                      flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors
                      ${isSelected ? 'bg-[#F4CC04]/10' : 'hover:bg-[#333333]'}
                    `}
                  >
                    <div className={`
                      w-4 h-4 rounded border flex items-center justify-center flex-shrink-0
                      ${isSelected
                        ? 'bg-[#F4CC04] border-[#F4CC04]'
                        : 'border-[#666666] bg-[#1a1a1a]'
                      }
                    `}>
                      {isSelected && <Check size={12} className="text-black" />}
                    </div>
                    <span className={`text-sm ${isSelected ? 'text-[#F4CC04]' : 'text-white'}`}>
                      {option.name}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
